import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { ImageOff } from "lucide-react";
import { useEffect, useState } from "react";

import { Spinner } from "./ui";

/** A foto da vistoria, buscada com o token e montada como blob.
 *
 * ## Por que não um `<img src="/api/.../file/">`
 *
 * Porque não funcionaria. A sessão do painel é JWT no cabeçalho
 * `Authorization` (lib/api.ts), e o navegador **não manda cabeçalho
 * nenhum** quando é ele que busca o `src` de uma imagem — a requisição
 * sairia anônima e voltaria 401. Cookie resolveria, e é justamente o que
 * este painel não usa.
 *
 * Então buscamos os bytes pelo mesmo cliente axios de todo o resto, que
 * já carrega o token, já renova o que expirou e já manda o `X-Garage`
 * de quem tem mais de uma oficina. O `blob:` que sai daí é local à aba:
 * não é link que se copie e mande para alguém.
 *
 * ## O revoke não é zelo
 *
 * Cada blob segura a foto inteira na memória da aba até alguém revogar.
 * São até 16 numa vistoria completa, a 1920px — o suficiente para o
 * celular do mecânico ficar lento com o modal aberto e fechado três
 * vezes. O cleanup roda na troca de foto e na desmontagem.
 */
export function PrivatePhoto({
  appointmentId,
  photoId,
  alt,
  className,
}: {
  appointmentId: number;
  photoId: number;
  alt: string;
  className?: string;
}) {
  const t = useT();
  const [url, setUrl] = useState<string | null>(null);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    // `cancelado` cobre o modal fechado antes de a foto chegar: sem ele,
    // o `setState` cairia num componente desmontado e o blob criado
    // ficaria sem ninguém para revogá-lo.
    let cancelado = false;
    let criada: string | null = null;
    setErro(false);
    setUrl(null);

    api
      .get(`/appointments/${appointmentId}/photos/${photoId}/file/`, {
        responseType: "blob",
      })
      .then(({ data }) => {
        if (cancelado) return;
        criada = URL.createObjectURL(data as Blob);
        setUrl(criada);
      })
      .catch(() => !cancelado && setErro(true));

    return () => {
      cancelado = true;
      if (criada) URL.revokeObjectURL(criada);
    };
  }, [appointmentId, photoId]);

  if (erro) {
    return (
      <div
        className={className}
        title={t("photos.failed")}
        role="img"
        aria-label={t("photos.failed")}
      >
        <div className="flex size-full items-center justify-center bg-carbon-900 text-steel-dim">
          <ImageOff size={18} />
        </div>
      </div>
    );
  }

  if (!url) {
    return (
      <div className={className} aria-busy="true">
        <div className="flex size-full items-center justify-center bg-carbon-900">
          <Spinner className="size-4" />
        </div>
      </div>
    );
  }

  return <img src={url} alt={alt} className={className} />;
}
